import { localize, Logger } from '@saviynt/common';
import moment from 'moment';

import { DATE_COLUMNS } from '../../../utils/constant';
import { certsFormattedMessage, getMessageKey } from '../../../utils/utils';

import {
  ADDED_FILTER,
  APPLIED_TO_EO_TYPE_AND_ORDER,
  APPLIED_TO_TYPE_AND_ORDER,
  COUNT_DEFAULT_FILTERS,
  COUNT_SELECTED_FILTERS,
  REMOVED_FILTER,
  UNCHANGED,
} from './constant';

// First argument should be previous state, and second argument should be updated state
export const compareTwoStatesForFilter = (state, updatedState) => {
  if (state.length === updatedState.length) {
    return {
      state: UNCHANGED,
      filter: null,
    };
  }

  if (state.length > updatedState.length) {
    return {
      state: REMOVED_FILTER,
      filter: state.filter(
        (item1) =>
          !updatedState.some(
            (item2) => item2.key === item1.key && item2.value === item1.value
          )
      ),
    };
  }

  return {
    state: ADDED_FILTER,
    filter: updatedState.filter(
      (item1) =>
        !state.some(
          (item2) => item2.key === item1.key && item2.value === item1.value
        )
    ),
  };
};

/**
 *
 * @param {*} obj object to be sorted
 * @returns  sorted object
 */
export const sortObjectByKeys = (obj, priorityKeys = []) => {
  const sortedObj = {};

  if (!obj) {
    return sortedObj;
  }

  // First add prioritized keys in order
  priorityKeys.forEach((key) => {
    if (obj[key]) {
      sortedObj[key] = obj[key];
    }
  });

  // Get all other keys and sort them alphabetically
  const otherKeys = Object.keys(obj).filter(
    (key) => !priorityKeys.includes(key)
  );
  // .sort(); remove filter not required

  // Iterate through the other keys and add to the sorted object
  otherKeys.forEach((key) => {
    sortedObj[key] = obj[key];
  });

  return sortedObj;
};

const getAppliedToText = (columnPickerList, key, l, isEOCampaign = false) => {
  const appliedToList = [];

  // TODO: @Jacob, @Sagar, CR-17904, Testing and dev env is complaining about columnPickerList, but only sometimes.
  if (!columnPickerList || typeof columnPickerList !== 'object') {
    Logger.warn('Invalid columnPickerList:', columnPickerList);

    return 'Applied to: None';
  }

  Object.entries(columnPickerList).forEach((column) => {
    const appliedToName = column[0];
    const appliedToObject = column[1];

    if (
      Object.hasOwnProperty.call(appliedToObject, key) &&
      !appliedToList.includes(appliedToName)
    ) {
      appliedToList.push(appliedToName);
    }
  });

  const sortedAppliedToList = appliedToList.sort((a, b) => {
    if (isEOCampaign) {
      return (
        APPLIED_TO_EO_TYPE_AND_ORDER.indexOf(a) -
        APPLIED_TO_EO_TYPE_AND_ORDER.indexOf(b)
      );
    }

    return (
      APPLIED_TO_TYPE_AND_ORDER.indexOf(a) -
      APPLIED_TO_TYPE_AND_ORDER.indexOf(b)
    );
  });

  const types = `${sortedAppliedToList
    ?.map((rowType) => l(rowType))
    .join(', ')}`;

  return certsFormattedMessage(l('Applied to: {types}', { types }), { types });
};

const extractDate = (key) => {
  const match = key.match(/\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}/);

  return match ? match[0] : null;
};

// TODO: @Jacob, @Vishnu CR-17904, Dates from BE causing depriciation warning in Jest test results, Parse and Sanitize from Front End required. Might want a ticket for this.
export const reCreateFilterOptions = (filterKey, options, baseSettings) => {
  const tempOptions = [];

  // eslint-disable-next-line no-restricted-syntax
  for (const key in options) {
    if (Object.prototype.hasOwnProperty.call(options, key)) {
      let fieldValue = key;

      if (
        DATE_COLUMNS.includes(filterKey) &&
        key.trim() !== '' &&
        key !== 'NoResponse'
      ) {
        const extractedDate = extractDate(key); // Extract valid date string

        if (extractedDate) {
          const date = moment(extractedDate, 'YYYY-MM-DD HH:mm:ss', true); // Strict format parsing

          if (date.isValid()) {
            fieldValue = date
              .utc()
              .local()
              .format(
                `${baseSettings.globalDateFormat?.toUpperCase()} HH:mm:ss`
              );
          } else {
            Logger.warn(`Invalid date format: ${key}`);
            fieldValue = key; // Fallback to original value
          }
        } else {
          Logger.warn(`Date not found in key: ${key}`);
          fieldValue = key;
        }
      }

      if (key.trim() === '') fieldValue = 'Blank';

      tempOptions.push({
        label: fieldValue,
        value: `${key}`,
        isChecked: false,
      });
    }
  }

  return tempOptions;
};

export const sanitizeAdvancedFilters = (
  filters,
  baseSettings,
  intl,
  messages,
  l,
  isEOCampaign,
  isDefaultColumnLabelBased = false
) => {
  const { valueCountMap, messageKeys } = filters;

  let COUNT_OF_FIXED_FILTERS = Math.min(
    COUNT_SELECTED_FILTERS,
    COUNT_DEFAULT_FILTERS
  );
  let count = 0;
  const tempFilters = [];

  let priorityKeys = [];

  if (isDefaultColumnLabelBased) {
    if (isEOCampaign) {
      priorityKeys = ['Ent_LABEL', 'Account_LABEL', 'Ent2_LABEL'];
    } else {
      priorityKeys = ['User_LABEL', 'Account_LABEL', 'Ent_LABEL', 'Role_LABEL'];
    }
  } else if (isEOCampaign) {
    priorityKeys = [
      'Ent_DISPLAYNAME',
      'Account_DISPLAYNAME',
      'Ent2_DISPLAYNAME',
    ];
  } else {
    priorityKeys = [
      'User_DISPLAYNAME',
      'Account_DISPLAYNAME',
      'Ent_DISPLAYNAME',
      'Role_DISPLAYNAME',
    ];
  }

  const defaultCount = priorityKeys.filter(
    (key) =>
      valueCountMap && typeof valueCountMap === 'object' && valueCountMap[key]
  ).length;

  COUNT_OF_FIXED_FILTERS = defaultCount;

  const sortedValueCountMap = sortObjectByKeys(valueCountMap, priorityKeys);

  Object.keys(sortedValueCountMap).forEach((key) => {
    const options = reCreateFilterOptions(
      key,
      sortedValueCountMap[key],
      baseSettings
    );

    tempFilters.push({
      key: `${key}`,
      label: `${
        messages[messageKeys[key]] || localize(intl, getMessageKey(key))
      }`,
      isActive: count < COUNT_OF_FIXED_FILTERS,
      isDefault: count < COUNT_OF_FIXED_FILTERS && false,
      isCustom: false,
      appliedTo: getAppliedToText(
        baseSettings.columnPickerList,
        key,
        l,
        isEOCampaign
      ),
      initialCount: options?.length || 0,
      options,
    });

    count += 1;
  });

  return tempFilters;
};

export const getActiveFiltersCount = (filters) =>
  filters.filter((filter) => filter.isActive === true && !filter.isCustom)
    .length;

// to check enable save button for personalization
export const isPersonalizationSaveEnabled = (firstArray, secondArray) => {
  // when personlized values are not exist
  if (!Array.isArray(firstArray)) {
    if (secondArray.length === 0) {
      return false;
    }

    return true;
  }

  const values1 = firstArray?.map((item) => item.value).sort();
  const values2 = secondArray?.map((item) => item.value).sort();

  if (values1?.length !== values2?.length) {
    return true;
  }

  return !values1?.every((val, index) => val === values2[index]);
};

/**
 * CR-20783 - to get default filters while switching b/w default to personalization views and vice versa
 * @param {*} filters
 * @returns - Active filters based on priority keys
 */
export const applyDefaultActiveFilters = (filters) => {
  const MAX_ACTIVE = 4;
  const PRIORITY_KEYS = [
    // 'User_DISPLAYNAME',
    // 'Account_DISPLAYNAME',
    // 'Ent_DISPLAYNAME',
    // 'Role_DISPLAYNAME',
    // 'Ent2_DISPLAYNAME',

    // change here as well
    'User_LABEL',
    'Account_LABEL',
    'Ent_LABEL',
    'Role_LABEL',
    'Ent2_LABEL',
  ];

  const filterMap = new Map();
  let nonCertifiedActiveCount = 0;

  // Single pass: index filters & count NON-CERTIFIED actives
  filters.forEach((filter) => {
    filterMap.set(filter.key, filter);

    if (filter.isActive && filter.key !== 'CERTIFIED') {
      nonCertifiedActiveCount += 1;
    }
  });

  // Already at limit (excluding CERTIFIED)
  if (nonCertifiedActiveCount >= MAX_ACTIVE) {
    return filters;
  }

  // Check if ANY priority key exists
  const hasAnyPriorityKey = PRIORITY_KEYS.some((key) => filterMap.has(key));

  // No priority key → keep existing state
  if (!hasAnyPriorityKey) {
    return filters;
  }

  // Activate priority keys up to 4 (excluding CERTIFIED)
  PRIORITY_KEYS.some((key) => {
    if (nonCertifiedActiveCount >= MAX_ACTIVE) return true;

    const filter = filterMap.get(key);

    if (filter && !filter.isActive && filter.key !== 'CERTIFIED') {
      filter.isActive = true;
      nonCertifiedActiveCount += 1;
    }

    return false;
  });

  return filters;
};
